import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Stack,
    Divider
} from '@mui/material';
import { useQuery } from 'react-query';
import { HttpService } from 'core/services/http';

const EXPERIENCE_LEVELS = [
    { value: 'Internship', label: 'Internship' },
    { value: 'Junior', label: 'Junior' },
    { value: 'Regular', label: 'Regular' },
    { value: 'Senior', label: 'Senior' },
    { value: 'Expert', label: 'Expert' },
    { value: 'Associate', label: 'Associate' }
];

function CandidatesFilterSidebar({ filters, onFilterChange, onApplyFilters, onResetFilters, appliedFilters, onRemoveFilter }) {
    const [localFilters, setLocalFilters] = useState(filters || {
        country: '',
        city: '',
        skill: '',
        experience_level: '',
        title: ''
    });

    // Sync local filters when filters prop changes
    useEffect(() => {
        if (filters) {
            setLocalFilters(filters);
        }
    }, [filters]);

    // Load filter options
    const { data: countriesData } = useQuery('countries', async () => {
        const service = new HttpService();
        const data = await service.get('/base/country/', null);
        return Array.isArray(data) ? data : (data?.results || []);
    }, { 
        staleTime: 24 * 60 * 60 * 1000, // 24 hours - countries rarely change
        cacheTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });

    const countries = React.useMemo(() => {
        const cts = countriesData?.results || [];
        return Array.isArray(cts) ? cts : (Array.isArray(countriesData) ? countriesData : []);
    }, [countriesData]);

    const { data: skillsData } = useQuery('skills', async () => {
        const service = new HttpService();
        const data = await service.get('/job/cv/skill/', null);
        return Array.isArray(data) ? data : (data?.results || []);
    }, { staleTime: 5 * 60 * 1000 });

    const skills = React.useMemo(() => {
        const sks = skillsData?.results || [];
        return Array.isArray(sks) ? sks : (Array.isArray(skillsData) ? skillsData : []);
    }, [skillsData]);

    const handleLocalFilterChange = (name, value) => {
        setLocalFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleApply = () => {
        const cleanedFilters = Object.entries(localFilters).reduce((acc, [key, value]) => {
            if (value !== '' && value !== null && value !== undefined) {
                acc[key] = value;
            }
            return acc;
        }, {});
        
        if (onApplyFilters) {
            onApplyFilters(cleanedFilters);
        }
    };

    const handleReset = () => {
        const resetFilters = {
            country: '',
            city: '',
            skill: '',
            experience_level: '',
            title: ''
        };
        setLocalFilters(resetFilters);
        if (onResetFilters) {
            onResetFilters();
        }
    };

    const getFilterLabel = (key, value) => {
        if (key === 'country') {
            const country = countries.find(c => c.id === parseInt(value));
            return country ? country.title : value;
        }
        if (key === 'skill') {
            const skill = skills.find(s => s.id === parseInt(value));
            return skill ? skill.title : value;
        }
        if (key === 'experience_level') {
            const level = EXPERIENCE_LEVELS.find(l => l.value === value);
            return level ? level.label : value;
        }
        return value;
    };

    const activeCount = Object.keys(appliedFilters || {}).length;

    return (
        <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body1" fontWeight={600}>
                    Filters {activeCount > 0 && `(${activeCount})`}
                </Typography>
                {activeCount > 0 && (
                    <Button size="small" onClick={handleReset} sx={{ textTransform: 'none', fontSize: '0.72rem', p: 0, minWidth: 0 }}>
                        Clear all
                    </Button>
                )}
            </Box>

            {activeCount > 0 && (
                <Stack direction="row" spacing={0} flexWrap="wrap" sx={{ mb: 2, gap: 0.5 }}>
                    {Object.entries(appliedFilters).map(([key, value]) => (
                        <Chip
                            key={key}
                            label={getFilterLabel(key, value)}
                            onDelete={() => onRemoveFilter && onRemoveFilter(key)}
                            size="small"
                            sx={{ fontSize: '0.7rem', height: 22 }}
                        />
                    ))}
                </Stack>
            )}

            <Stack spacing={2}>
                <TextField
                    label="Job Title"
                    value={localFilters.title || ''}
                    onChange={(e) => handleLocalFilterChange('title', e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="e.g. Software Developer"
                />

                <FormControl fullWidth size="small">
                    <InputLabel>Country</InputLabel>
                    <Select
                        value={localFilters.country || ''}
                        label="Country"
                        onChange={(e) => handleLocalFilterChange('country', e.target.value)}
                    >
                        <MenuItem value="">All Countries</MenuItem>
                        {countries.map((country) => (
                            <MenuItem key={country.id} value={country.id}>{country.title}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    label="City"
                    value={localFilters.city || ''}
                    onChange={(e) => handleLocalFilterChange('city', e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="e.g. London"
                />

                <FormControl fullWidth size="small">
                    <InputLabel>Skill</InputLabel>
                    <Select
                        value={localFilters.skill || ''}
                        label="Skill"
                        onChange={(e) => handleLocalFilterChange('skill', e.target.value)}
                    >
                        <MenuItem value="">All Skills</MenuItem>
                        {skills.map((skill) => (
                            <MenuItem key={skill.id} value={skill.id}>{skill.title}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                    <InputLabel>Experience Level</InputLabel>
                    <Select
                        value={localFilters.experience_level || ''}
                        label="Experience Level"
                        onChange={(e) => handleLocalFilterChange('experience_level', e.target.value)}
                    >
                        <MenuItem value="">All Levels</MenuItem>
                        {EXPERIENCE_LEVELS.map((level) => (
                            <MenuItem key={level.value} value={level.value}>{level.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Divider />

                <Stack direction="row" spacing={1}>
                    <Button
                        variant="contained"
                        onClick={handleApply}
                        disableElevation
                        fullWidth
                        size="small"
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Apply
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleReset}
                        fullWidth
                        size="small"
                        sx={{ textTransform: 'none' }}
                    >
                        Reset
                    </Button>
                </Stack>
            </Stack>
        </Paper>
    );
}

export default CandidatesFilterSidebar;

